import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'
import { retainNewerStageRead } from '../api/queries'

// The Review stage's reads (demo `approval-api.ts`, `approval-types.ts` and the AI review query).
// Review is the AI review (USA-1333); a started project's POs are reviewed on the project.

type Schemas = components['schemas']

export type ReviewWorkspace = Schemas['QbReviewWorkspaceRead']
export type ProjectStaff = Schemas['QbPurchaseOrderStaffRead']
export type QuoteAiReview = Schemas['QbQuoteAiReviewRead']
export type AiReviewFinding = Schemas['QbAiReviewFinding']

export const reviewQueryKey = (quoteId: string) => ['quote-review', quoteId] as const

export const reviewQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: reviewQueryKey(quoteId),
    queryFn: ({ signal }) => api.request<ReviewWorkspace>(`/quotes/${quoteId}/review`, { signal }),
    structuralSharing: retainNewerStageRead as (previous: unknown, next: unknown) => ReviewWorkspace,
  })

/** Approve the AI review of the revision Sales is looking at: the Review stage. */
export function approveAiReview(api: QuoteBuilderApi, quoteId: string, expectedRevision: number) {
  return api.request<ReviewWorkspace>(`/quotes/${quoteId}/ai-review/approve`, {
    method: 'POST',
    body: JSON.stringify({ expected_revision: expectedRevision }),
  })
}

/** Assign one project role on the quote; not a quote change, so a published final quote stays current. */
export function assignProjectStaff(
  api: QuoteBuilderApi,
  quoteId: string,
  role: keyof ProjectStaff,
  userId: number | null,
) {
  return api.request<ReviewWorkspace>(`/quotes/${quoteId}/review/staff`, {
    method: 'PUT',
    body: JSON.stringify({ role, user_id: userId }),
  })
}

export const aiReviewQueryKey = (quoteId: string, revision: number) =>
  ['approval-ai-review', quoteId, revision] as const

/** Start (POST) or poll (GET) the AI review of one revision (demo `requestApprovalReview`). */
export function requestAiReview(
  api: QuoteBuilderApi,
  quoteId: string,
  revision: number,
  options: { poll?: boolean; retry?: boolean; signal?: AbortSignal } = {},
) {
  const path = `/quotes/${quoteId}/ai-review`
  return options.poll
    ? api.request<QuoteAiReview>(`${path}?revision=${revision}`, {
        signal: options.signal,
      })
    : api.request<QuoteAiReview>(path, {
        method: 'POST',
        signal: options.signal,
        body: JSON.stringify({
          expected_revision: revision,
          retry: options.retry ?? false,
        }),
      })
}
